import React, { useMemo } from 'react';
import { Navigation } from '@shopify/polaris';
import { AlertBubbleIcon } from '@shopify/polaris-icons';
import { useLocation } from 'react-router-dom';
import { useMissingInvoices } from '../../missingInvoices/hooks/useMissingInvoices';
import { useCollectionsPermissions } from '../../shared/useCollectionsPermissions';
import { getCollectionsSelectedLocation } from './CollectionsLocationToggle';

type SidebarSubItem = {
  label: string;
  url: string;
  disabled: boolean;
  badge?: string;
};

type SidebarItem = {
  label: string;
  url: string;
  icon: any;
  matches: boolean;
  subNavigationItems: SidebarSubItem[];
};

const CollectionsSidebar: React.FC = () => {
  const location = useLocation();
  const { isCollectionsAdmin } = useCollectionsPermissions();
  const selectedLocation = getCollectionsSelectedLocation();
  const { data: missingInvoicesData } = useMissingInvoices(selectedLocation, isCollectionsAdmin);
  const missingInvoicesCount = missingInvoicesData?.data?.length ?? 0;
  const missingInvoicesBadgeDisplay = missingInvoicesCount > 0 ? String(missingInvoicesCount) : undefined;

  const navigationItems = useMemo(() => {
    const collectionsSubItems: SidebarSubItem[] = [{ label: 'Clients', url: '/collections/clients', disabled: false }];

    if (isCollectionsAdmin) {
      collectionsSubItems.splice(1, 0, { label: 'Departments', url: '/collections/departments', disabled: false });
      collectionsSubItems.push(
        { label: 'Configuration', url: '/collections/configuration', disabled: false },
        { label: 'Scheduled Emails', url: '/collections/scheduled-emails', disabled: false },
      );
    }

    const statementsSubItems: SidebarSubItem[] = isCollectionsAdmin
      ? [
          { label: 'Clients', url: '/collections/statements', disabled: false },
          { label: 'Configuration', url: '/collections/statements/configuration', disabled: false },
          { label: 'Scheduled Emails', url: '/collections/statements/scheduled-emails', disabled: false },
        ]
      : [];

    const collectionsAndStatementsSubItems: SidebarSubItem[] = [
      { label: 'Email History', url: '/collections/email-history', disabled: false },
    ];

    const items: SidebarItem[] = [
      {
        label: 'Collections',
        url: '/collections/clients',
        icon: AlertBubbleIcon as any,
        matches: true,
        subNavigationItems: collectionsSubItems,
      },
      ...(statementsSubItems.length > 0
        ? [
            {
              label: 'Statements',
              url: '/collections/statements',
              icon: AlertBubbleIcon as any,
              matches: true,
              subNavigationItems: statementsSubItems,
            },
          ]
        : []),
      {
        label: 'Collections & Statements',
        url: '/collections/email-history',
        icon: AlertBubbleIcon as any,
        matches: true,
        subNavigationItems: collectionsAndStatementsSubItems,
      },
    ];

    if (isCollectionsAdmin) {
      items.push({
        label: 'Admin',
        url: '/collections/servman_sql_logs',
        icon: AlertBubbleIcon as any,
        matches: true,
        subNavigationItems: [
          { label: 'Servman SQL Logs', url: '/collections/servman_sql_logs', disabled: false },
          {
            label: 'Missing Invoices',
            url: '/collections/missing-invoices',
            disabled: false,
            badge: missingInvoicesBadgeDisplay,
          },
        ],
      });
    }

    return items;
  }, [isCollectionsAdmin, missingInvoicesBadgeDisplay]);

  return (
    <Navigation location={location.pathname}>
      <Navigation.Section items={navigationItems} />
    </Navigation>
  );
};

export default CollectionsSidebar;
